import { Component, EventEmitter, OnInit, Output, Input, OnDestroy } from '@angular/core';
import { FormGroup, FormGroupDirective } from '@angular/forms';
import { BehaviorSubject, Observable } from 'rxjs';
import { NotificationType } from 'src/app/shared/app.enums';
import { EventNotification } from 'src/app/shared/models/event-notification';
import { DomSanitizer } from '@angular/platform-browser';

@Component({
  selector: 'ness-event-summary',
  templateUrl: './event-summary.component.html',
  styles: ['.btn.btn-link { font-size: 12px; }']
})
export class EventSummaryComponent implements OnInit, OnDestroy {
  private notifications: EventNotification[] = [];
  private _eventNotification: BehaviorSubject<EventNotification[]> = new BehaviorSubject<EventNotification[]>([]);
  public eventNotification$: Observable<EventNotification[]> = this._eventNotification.asObservable();
  eventForm: FormGroup = this.controlContainer.control as FormGroup;
  submitNotification: EventNotification = { header: 'Success: Event record created.', message: '', NotificationType: NotificationType.success };
  @Input() submitted: boolean = false;
  @Output() targetStep: EventEmitter<string> = new EventEmitter();

  constructor(private controlContainer: FormGroupDirective,
    private domSanitizer: DomSanitizer) { }

  ngOnInit() {
    this.notifications = [
      {
        header: 'Event details',
        message: null,
        NotificationType: NotificationType.default,
        RouteLink: 'details'
      },
      {
        header: 'Location information',
        message: null,
        NotificationType: NotificationType.default,
        RouteLink: 'locations'
      },
      {
        header: 'People information',
        message: null,
        NotificationType: NotificationType.default,
        RouteLink: 'people'
      },
      {
        header: 'Attachment information',
        message: null,
        NotificationType: NotificationType.default,
        RouteLink: 'attachments'
      }
    ];

    this.setNotifications();
  }

  ngOnDestroy() {
    this.resetNotifications();
  }

  public navigateToStep(stepName: string | any[]): void {
    if (typeof (stepName) === 'string') {
      this.targetStep.emit(stepName);
    } else {
      void 0;
    }
  }

  public setNotifications(): void {
    if (!this.eventForm)
      void 0;

    if (this.eventForm.invalid && !this.eventForm.get('ENESS_XREF_EventsPeople').hasError('missingPersonType')) {
      this.notifications[0].NotificationType = NotificationType.alert;
      let eventDetailsMessage = 'Please review the event details.';

      eventDetailsMessage += '<ul>';
      if (this.eventForm.get('EventDateOffset').hasError('required')) {
        eventDetailsMessage += '<li>The event date is missing.</li>';
      }
      if (this.eventForm.get('TimeZoneID').hasError('required')) {
        eventDetailsMessage += '<li>The event date time zone is missing.</li>';
      }
      if (this.eventForm.get('Summary').hasError('maxlength')) {
        eventDetailsMessage += '<li>The event summary exceeds the max length allowed.</li>';
      }
      if (this.eventForm.get('Narrative').hasError('maxlength')) {
        eventDetailsMessage += '<li>The event narrative exceeds the max length allowed.</li>';
      }
      if (this.eventForm.get('StatusID').hasError('required')) {
        eventDetailsMessage += '<li>The event status is missing.</li>';
      }
      if (this.eventForm.get('OtherStatus').hasError('maxlength')) {
        eventDetailsMessage += '<li>The event Other status exceeds the max length allowed.</li>';
      }
      if (this.eventForm.get('CrimeTypeID').hasError('required')) {
        eventDetailsMessage += '<li>The event crime type is missing.</li>';
      }
      if (this.eventForm.get('OtherCrimeType').hasError('maxlength')) {
        eventDetailsMessage += '<li>The event Other crime type exceeds the max length allowed.</li>';
      }
      if (this.eventForm.get('ENESS_XREF_LECaseNumbers.LECaseNumber').hasError('maxlength')) {
        eventDetailsMessage += '<li>The event LE case number exceeds the max length allowed.</li>';
      }
      if (this.eventForm.get('ENESS_XREF_LECaseNumbers.LEAlternateCaseNumber').hasError('maxlength')) {
        eventDetailsMessage += '<li>The event alternate LE case number exceeds the max length allowed.</li>';
      }
      if (this.eventForm.get('ENESS_REF_Agency.AgencyID').hasError('required')) {
        eventDetailsMessage += '<li>The event agency is missing.</li>';
      }

      eventDetailsMessage += '</ul>';

      this.notifications[0].message = this.domSanitizer.bypassSecurityTrustHtml(eventDetailsMessage);
    }

    if (!this.eventForm.get('ENESS_XREF_EventsLocations').value || this.eventForm.get('ENESS_XREF_EventsLocations').value.length == 0) {
      this.notifications[1].NotificationType = NotificationType.default;
      this.notifications[1].message = this.domSanitizer.bypassSecurityTrustHtml('This event currently has no associated locations.');
    }

    if (!this.eventForm.get('ENESS_XREF_EventsPeople').value || this.eventForm.get('ENESS_XREF_EventsPeople').value.length == 0) {
      this.notifications[2].NotificationType = NotificationType.default;
      this.notifications[2].message = this.domSanitizer.bypassSecurityTrustHtml('This event currently has no associated people.');
    }

    if ((this.eventForm.get('ENESS_XREF_EventsPeople').errors && Object.keys(this.eventForm.get('ENESS_XREF_EventsPeople').errors).length > 0) || this.isMissingPersonType()) {
      this.notifications[2].NotificationType = NotificationType.alert;
      let eventPeopleMessage = 'Please review the event people.'
        eventPeopleMessage += '<ul><li>Event person type is missing for one or more people.</li></ul>';

      this.notifications[2].message = this.domSanitizer.bypassSecurityTrustHtml(eventPeopleMessage);
    }

    if (!this.eventForm.get('ENESS_XREF_EventAttachments').value || this.eventForm.get('ENESS_XREF_EventAttachments').value.length == 0) {
      this.notifications[3].NotificationType = NotificationType.default;
      this.notifications[3].message = this.domSanitizer.bypassSecurityTrustHtml('This event currently has no attachments.');
    }

    this._eventNotification.next(this.notifications);
  }

  public resetNotifications(): void {
    this.notifications.forEach(x => {
      x.message = null;
      x.NotificationType = NotificationType.default;
    });
    this._eventNotification.next(this.notifications);
  }

  public isMissingPersonType(): boolean {
    let eventPeople = this.eventForm.get('ENESS_XREF_EventsPeople').value;
    return !!eventPeople ? eventPeople.some((p: ServerModels.ENESS_XREF_EventsPeople) => p.ENESS_XREF_EventsPeopleTypes && p.ENESS_XREF_EventsPeopleTypes.length === 0) : false;
  }

}
